const howItWorksPlaybackRequests = new WeakMap();

const playHowItWorksVideo = (video) => {
  if (!video) return Promise.resolve(false);

  const pendingRequest = howItWorksPlaybackRequests.get(video);
  if (pendingRequest) return pendingRequest;

  const playRequest = video.play();
  if (!playRequest || typeof playRequest.then !== 'function') {
    return Promise.resolve(true);
  }

  const handledRequest = playRequest
    .then(() => true)
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('How it works video playback failed:', error);
      }

      return false;
    })
    .finally(() => {
      if (howItWorksPlaybackRequests.get(video) === handledRequest) {
        howItWorksPlaybackRequests.delete(video);
      }
    });

  howItWorksPlaybackRequests.set(video, handledRequest);
  return handledRequest;
};

const loadHowItWorksVideo = (video) => {
  if (!video || video.dataset.videoLoaded === 'true') return;

  const sources = video.querySelectorAll('source[data-src]');

  sources.forEach((source) => {
    source.src = source.dataset.src;

    if (source.dataset.type) {
      source.type = source.dataset.type;
    }
  });

  video.dataset.videoLoaded = 'true';
  video.load();
};

class HowItWorksVideo {
  constructor() {
    this.init();
  }

  init() {
    this.setupVideoControls();
    this.setupVideoModal();
  }

  setupVideoControls() {
    // Find all main videos and their corresponding controls
    const mainVideos = document.querySelectorAll('[data-video="main"]');

    mainVideos.forEach(video => {
      const control = video.parentElement.querySelector('[data-how-it-works-video-pause]');
      if (!control) return;

      video.dataset.userPaused = 'false';

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadHowItWorksVideo(video);

            if (video.dataset.userPaused !== 'true') {
              playHowItWorksVideo(video);
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      }, {
        threshold: 0.25,
        rootMargin: '300px 0px'
      });

      observer.observe(video.parentElement);

      // Handle control clicks
      control.addEventListener('click', () => {
        if (video.paused) {
          video.dataset.userPaused = 'false';
          loadHowItWorksVideo(video);
          playHowItWorksVideo(video);
        } else {
          video.dataset.userPaused = 'true';
          video.pause();
        }
      });

      // Update control state when video plays/pauses
      video.addEventListener('play', () => {
        this.updateControlState(control, 'pause');
      });

      video.addEventListener('pause', () => {
        this.updateControlState(control, 'play');
      });

      // Set initial state based on video state
      const initialState = video.paused ? 'play' : 'pause';
      this.updateControlState(control, initialState);
    });
  }

  updateControlState(control, state) {
    const svg = control.querySelector('svg');
    if (!svg) return;

    // Remove existing main video state classes
    control.classList.remove('how-it-works__video-control--main-play', 'how-it-works__video-control--main-pause');

    if (state === 'play') {
      control.classList.add('how-it-works__video-control--main-play');
      svg.innerHTML = `
        <circle cx="18" cy="18" r="17.5" fill="rgba(0, 0, 0, 0.1)" stroke="rgba(0, 0, 0, 0.2)"/>
        <polygon points="13.5,11.5 13.5,24.5 24.5,18" fill="white"/>
      `;
      svg.setAttribute('width', '36');
      svg.setAttribute('height', '36');
      svg.setAttribute('viewBox', '0 0 36 36');
    } else {
      control.classList.add('how-it-works__video-control--main-pause');
      svg.innerHTML = `
        <circle cx="12" cy="12" r="11.5" fill="rgba(0, 0, 0, 0.1)" stroke="rgba(0, 0, 0, 0.2)"/>
        <rect x="9" y="8" width="2" height="8" fill="white"/>
        <rect x="13" y="8" width="2" height="8" fill="white"/>
      `;
      svg.setAttribute('width', '24');
      svg.setAttribute('height', '24');
      svg.setAttribute('viewBox', '0 0 24 24');
    }
  }
}

// --- Modal logic ---
HowItWorksVideo.prototype.setupVideoModal = function() {
  const section = document.getElementById(`HowItWorks-${this.getSectionId()}`) || document.querySelector('.how-it-works');
  if (!section) return;

  const modal = document.getElementById(`HowItWorksModal-${this.getSectionId()}`) || document.querySelector('.how-it-works__modal');
  if (!modal) return;

  // Get all open buttons and secondary video wrappers
  const openButtons = section.querySelectorAll('[data-open-video-modal]');
  const closeTargets = modal.querySelectorAll('[data-close-video-modal]');
  const mediaContainer = modal.querySelector('[data-video-modal-media]');
  const secondaryWrappers = section.querySelectorAll('.how-it-works__secondary-video');

  if (!openButtons.length || !mediaContainer || !secondaryWrappers.length) return;

  const buildEmbedUrl = (provider, videoId) => {
    if (provider === 'youtube') {
      return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`;
    }
    if (provider === 'vimeo') {
      return `https://player.vimeo.com/video/${encodeURIComponent(videoId)}?autoplay=1&title=0&byline=0&portrait=0`;
    }
    return '';
  };

  const openModal = (provider, videoId) => {
    const src = buildEmbedUrl(provider, videoId);
    if (!src) return;
    modal.removeAttribute('hidden');
    mediaContainer.innerHTML = `<iframe src="${src}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen title="How it works video"></iframe>`;
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    modal.setAttribute('hidden', '');
    mediaContainer.innerHTML = '';
    document.body.style.overflow = '';
  };

  // Set up click handlers for each open button
  openButtons.forEach((button, index) => {
    const correspondingWrapper = secondaryWrappers[index];
    if (!correspondingWrapper) return;

    const provider = correspondingWrapper.getAttribute('data-video-provider');
    const videoId = correspondingWrapper.getAttribute('data-video-id');
    if (!provider || !videoId) return;

    button.addEventListener('click', () => {
      openModal(provider, videoId);
    });
  });

  // Set up close handlers
  for (const target of closeTargets) {
    target.addEventListener('click', closeModal);
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
  });
};

HowItWorksVideo.prototype.getSectionId = function() {
  const host = document.querySelector('.how-it-works');
  if (!host) return null;
  const id = host.getAttribute('id');
  if (!id) return null;
  return id.replace('HowItWorks-', '');
};

// Also initialize if script loads after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new HowItWorksVideo();
  });
} else {
  new HowItWorksVideo();
}
